import { h } from '@omni-torn/ui';
import { keyMeets, OVERVIEW_CARDS } from '@omni-torn/shared';
import { readBars, readCooldowns, readMoney, readNetworth, readRefills, readStatus, readTravel, type Bar } from '../read';
import { session, torn } from '../session';
import { box, duration, facts, gated, loading, meter, money, problem, profileUrl, ext, tct } from '../ui';

type Card = keyof typeof OVERVIEW_CARDS;

/** The /user selections the key can read, for the cards asked for (one call for all of them). */
export function readable(cards: Card[]): string[] {
  const key = session.get()?.access ?? null;
  return cards.filter((c) => keyMeets(key, OVERVIEW_CARDS[c])).flatMap((c) => OVERVIEW_CARDS[c].user);
}

const BAR_KIND: Record<string, string> = { energy: 'energy', nerve: 'nerve', happy: 'happy', life: 'life' };

function fullIn(b: Bar): string {
  return b.fullAt ? `full in ${duration((b.fullAt - Date.now()) / 1000)} (${tct(b.fullAt)} TCT)` : b.current >= b.maximum ? 'full' : '';
}

export async function renderOverview(view: HTMLElement): Promise<void> {
  const grid = h('div', { class: 'cards' }, loading('Reading your Torn…'));
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Overview'), h('p', { class: 'muted' }, 'Live from Torn with your key. Refreshes every minute while open.')), grid);
  const draw = async () => {
    let body: unknown;
    try {
      body = await torn('/user/', { selections: readable(['status', 'bars', 'cooldowns', 'travel', 'refills', 'money', 'networth']).join(',') }, 55_000);
    } catch (err) {
      grid.replaceChildren(problem(err));
      return;
    }
    const now = Date.now();
    const st = readStatus(body);
    const bars = readBars(body, now);
    const cd = readCooldowns(body);
    const tr = readTravel(body);
    const rf = readRefills(body);
    const mo = readMoney(body);
    const nw = readNetworth(body);
    grid.replaceChildren(
      st
        ? box(
            'Status',
            [
              h('div', { class: 'status-line' }, h('span', { class: `status status-${st.color.replace(/[^a-z]/gi, '')}` }, st.state), ' ', st.description, st.until && st.until > now ? h('span', { class: 'muted' }, ` · out ${tct(st.until)} TCT`) : null),
              st.details ? h('p', { class: 'muted' }, st.details) : null,
              facts([
                ['Level', String(st.level ?? '—')],
                ['Faction', st.faction ? `${st.faction.name} · ${st.faction.position}` : 'None'],
                ['Life', st.life ? `${st.life.current.toLocaleString()} / ${st.life.maximum.toLocaleString()}` : '—'],
              ]),
            ],
            { id: 'status', extra: st.playerId ? ext(profileUrl(st.playerId), 'Profile ↗') : null },
          )
        : box('Status', h('p', { class: 'muted' }, 'Torn didn’t send your status.')),
      gated('Bars', OVERVIEW_CARDS.bars, 'energy, nerve, happy and life, and when they’re full.', () =>
        box(
          'Bars',
          [
            ...(['energy', 'nerve', 'happy', 'life'] as const).filter((k) => bars[k]).map((k) => meter(k[0]!.toUpperCase() + k.slice(1), bars[k]!.current, bars[k]!.maximum, BAR_KIND[k]!, fullIn(bars[k]!))),
            bars.chain && bars.chain.current > 0 ? h('p', {}, `Chain ${bars.chain.current}/${bars.chain.maximum}${bars.chain.timeout ? ` · ${duration(bars.chain.timeout)} left` : ''}`) : null,
          ],
          { id: 'bars' },
        ),
      ),
      gated('Cooldowns', OVERVIEW_CARDS.cooldowns, 'drug, booster and medical cooldowns.', () =>
        box(
          'Cooldowns',
          cd
            ? facts([
                ['Drug', cd.drug > 0 ? `${duration(cd.drug)} · ${tct(now + cd.drug * 1000)} TCT` : h('span', { class: 't-green' }, 'ready')],
                ['Booster', cd.booster > 0 ? `${duration(cd.booster)} · ${tct(now + cd.booster * 1000)} TCT` : h('span', { class: 't-green' }, 'ready')],
                ['Medical', cd.medical > 0 ? `${duration(cd.medical)} · ${tct(now + cd.medical * 1000)} TCT` : h('span', { class: 't-green' }, 'ready')],
              ])
            : h('p', { class: 'muted' }, '—'),
          { id: 'cooldowns' },
        ),
      ),
      gated('Travel', OVERVIEW_CARDS.travel, 'where you are flying and when you land.', () =>
        box(
          'Travel',
          !tr
            ? h('p', { class: 'muted' }, '—')
            : tr.timeLeft > 0
              ? facts([
                  ['Flying to', tr.destination],
                  ['Lands', tr.arrival ? `${tct(tr.arrival)} TCT · in ${duration(tr.timeLeft)}` : `in ${duration(tr.timeLeft)}`],
                  ['Ticket', tr.method || '—'],
                ])
              : h('p', {}, tr.destination === 'Torn' ? 'In Torn City.' : `In ${tr.destination}.`),
          { id: 'travel', extra: h('a', { href: '/travel', 'data-link': true }, 'Flight times') },
        ),
      ),
      gated('Refills', OVERVIEW_CARDS.refills, 'which of today’s refills are still unused.', () =>
        box(
          'Refills',
          rf
            ? facts([
                ['Energy', rf.energy ? 'used' : h('span', { class: 't-green' }, 'available')],
                ['Nerve', rf.nerve ? 'used' : h('span', { class: 't-green' }, 'available')],
                ['Token', rf.token ? 'used' : h('span', { class: 't-green' }, 'available')],
                ['Special', String(rf.special)],
              ])
            : h('p', { class: 'muted' }, '—'),
          { id: 'refills' },
        ),
      ),
      gated('Money', OVERVIEW_CARDS.money, 'cash, points, bank and vault.', () =>
        box(
          'Money',
          mo
            ? facts([
                ['Wallet', money(mo.wallet)],
                ['Points', mo.points?.toLocaleString() ?? '—'],
                ['City bank', mo.bank ? `${money(mo.bank.amount)}${mo.bank.until ? ` · back ${tct(mo.bank.until)}` : ''}` : '—'],
                ['Vault', money(mo.vault)],
                ['Cayman', money(mo.cayman)],
              ])
            : h('p', { class: 'muted' }, '—'),
          { id: 'money' },
        ),
      ),
      gated('Networth', OVERVIEW_CARDS.networth, 'your networth and what it’s made of.', () =>
        box(
          'Networth',
          nw ? [h('div', { class: 'big-number' }, money(nw.total)), facts(nw.parts.slice(0, 8).map((p) => [p.label, money(p.value)]))] : h('p', { class: 'muted' }, '—'),
          { id: 'networth' },
        ),
      ),
    );
  };
  await draw();
  const timer = setInterval(() => {
    if (!view.isConnected || !grid.isConnected) return clearInterval(timer);
    void draw();
  }, 60_000);
}
